import { INITIAL_STATE, ValidatorProgress, ValidatorsState } from "../core/state-shape";
import { http } from "../lib/http";
import { measure } from "@/lib/performance";
import { useReviewStore } from "./review";
import { toast } from 'sonner';
import { createProjectScopedStore } from "./projectScoped";
import useSyncStore from "./sync";
type ValidatorsActions = {
  setState: (newState: ValidatorsState) => void;
  patch: (p: Partial<ValidatorsState>) => void;
  reset: () => void;
  run: (input: { scope: string; types?: string[]; taskId?: string; proposalId?: string, files?: string[], applyOnSuccess?: boolean }) => Promise<string>;
  poll: (runId: string) => Promise<void>;
  runFullCoverage: () => Promise<string>;
  loadResultsForProject: (projectId: string) => Promise<void>;
  runTestsForFiles: (files: string[]) => Promise<string>;
};

export const useValidatorsStore = createProjectScopedStore<ValidatorsState & ValidatorsActions>('validators', (set) => ({
  ...INITIAL_STATE.validators,
  setState: (newState) => set(newState),
  patch: (p) => set(p),
  reset: () => set({ ...INITIAL_STATE.validators }),

  run: measure(async (input) => {
    const { selections, editedContent } = useReviewStore.getState();

    // New Step 1: Stage the reviewed content before validation.
    // This converts the proposal's diffs into full files based on user decisions.
    if (input.proposalId) {
      await http(`/api/proposals/${input.proposalId}/stage`, {
        method: 'POST',
        body: JSON.stringify({
          reviewSelections: selections,
          editedContent: editedContent || {},
        }),
      });
    }

    // Step 2: Run validators against the now-staged content.
    const payload = { ...input, reviewSelections: selections }; // selections are still useful to determine which files to validate
    const res = await http<{ runId: string; status: string }>(`/api/validators/run`, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    set((state) => {
      const newProgress: ValidatorProgress = {
        id: res.runId,
        taskId: input.taskId,
        status: "running",
        updatedAt: new Date().toISOString(),
      };
      return {
        running: {
          lint: !!input.types?.includes("lint"),
          typecheck: !!input.types?.includes("typecheck"),
          tests: !!input.types?.includes("tests"),
        },
        progress: {
          ...state.progress,
          [res.runId]: newProgress,
        },
      };
    });

    return res.runId;
  }, 'validators.run'),

  poll: measure(async (runId) => {
    try {
      const res = await http<{
        runId: string;
        status: "running" | "complete" | "failed";
        progress: number;
        phase: string;
        message: string;
        results?: any;
      }>(`/api/validators/${encodeURIComponent(runId)}/status`);

      set((state) => {
        const newProgress: ValidatorProgress = {
          ...(state.progress[runId] || {}),
          id: runId,
          status: res.status,
          percent: res.progress,
          phase: res.phase,
          updatedAt: new Date().toISOString(),
        };

        const newState: Partial<ValidatorsState> = {
          progress: { ...state.progress, [runId]: newProgress },
        };

        if (res.status === "complete" || res.status === "failed") {
          newState.running = { lint: false, typecheck: false, tests: false };
          if (res.results) {
            const taskId = state.progress[runId]?.taskId;
            newState.results = { ...state.results, latest: res.results };
            if (taskId) {
              newState.results.byTask = { ...state.results.byTask, [taskId]: res.results };
            }
          }
        }
        return newState;
      });
    } catch (error) {
      console.error(`Polling failed for runId ${runId}:`, error);
    }
  }, 'validators.poll'),

  runFullCoverage: measure(async () => {
      const toastId = toast.loading("Starting full project coverage scan...");
      try {
        const res = await http<{ runId: string; status: string }>(`/api/validators/run-full-coverage`, {
          method: "POST",
          body: JSON.stringify({}), // Body is no longer needed
        });

        set((state) => {
        const newProgress: ValidatorProgress = {
          id: res.runId,
          status: "running",
          updatedAt: new Date().toISOString(),
        };
        return {
          progress: {
            ...state.progress,
            [res.runId]: newProgress,
          },
        };
      });

      toast.success("Coverage scan started.", { id: toastId });
      return res.runId;
    } catch (e: any) {
      toast.error("Failed to start coverage scan.", { id: toastId, description: e.message });
      throw e;
    }
  }, 'validators.runFullCoverage'),

  runTestsForFiles: measure(async (files: string[]) => {
    const toastId = toast.loading("Starting test run...");
    try {
      const res = await http<{ runId: string; status: string }>('/api/validators/run-tests-for-files', {
        method: 'POST',
        body: JSON.stringify({ files }),
      });
      
      set((state) => {
        const newProgress: ValidatorProgress = {
          id: res.runId,
          status: "running",
          updatedAt: new Date().toISOString(),
        };
        return {
          progress: {
            ...state.progress,
            [res.runId]: newProgress,
          },
        };
      });

      return res.runId;
    } catch (e: any) {
      toast.error("Failed to start test run.", { id: toastId, description: e.message });
      throw e;
    }
  }, 'validators.runTestsForFiles'),

  loadResultsForProject: measure(async (projectId: string) => {
    // try {
    //   const res = await http<ValidatorResults>(`/api/validators/results?project_id=${encodeURIComponent(projectId)}`);
    //   set((state) => ({
    //     results: {
    //       ...state.results,
    //       latest: res,
    //     },
    //   }));
    // } catch (e: any) {
    //   console.error("Failed to load project validator results", e);
    //   toast.error("Failed to load validator results.", { description: e.message });
    // }
  }, 'validators.loadResultsForProject'),
}));